import {
  invalidateTimesheetQueries,
  timesheetKeys,
} from "components/timesheet/queries";
import { createTimesheetEntry, createTimesheetEntries } from "./api";
import { queryClient } from "utils/react-query";
import { selectUser } from "store";
import { toast } from "sonner";
import { useAcknowledgementMutation } from "components/timesheet/acknowledgement/mutation";
import { useMutation } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { invalidateAllQueries } from "utils/utils";

function buildPayload(values, user, date) {
  return {
    ...Object.fromEntries(Object.entries(values).filter(([_, v]) => v !== "")),
    employee: user.id,
    consumption_factor: user.consumption_factor,
    date,
    logged_by_employee: true,
    // Auto-set approved_hours to match logged_hours on creation
    // approved_hours: values.logged_hours,
  };
}

export function useLogTimesheetEntry() {
  const user = useSelector(selectUser);
  const acknowledgementMutation = useAcknowledgementMutation();

  return useMutation({
    mutationFn: async ({ values }) => {
      const date = values.date;
      const payload = buildPayload(values, user, date);

      const response = await createTimesheetEntry({ item: payload });

      // Sync daily work capacity after successful creation
      await acknowledgementMutation.mutateAsync(date);

      return { response, date };
    },
    onSuccess: () => {
      toast.success("Entry Created Successfully");
      invalidateAllQueries();
    },
    onError: (error) => {
      if (acknowledgementMutation.isError) {
        toast.error("Entry created but acknowledgement failed");
      } else {
        toast.error(error.message);
      }
      console.error("Error submitting timesheet entry:", error);
    },
  });
}

export function useLogTimesheetEntries() {
  const user = useSelector(selectUser);
  const acknowledgementMutation = useAcknowledgementMutation();

  return useMutation({
    mutationFn: async ({ values }) => {
      const date = values.date;
      const payloads = values.entries.map((entry) => {
        const fullValues = {
          date: values.date,
          project: values.project,
          job: values.job,
          milestone: values.milestone,
          tag: entry.tag,
          logged_hours: entry.logged_hours,
          description: entry.description,
          created_from_draft: values.created_from_draft,
        };
        return buildPayload(fullValues, user, date);
      });

      const response = await createTimesheetEntries({ items: payloads });

      // Sync daily work capacity after successful creation
      await acknowledgementMutation.mutateAsync(date);

      return { response, date };
    },
    onSuccess: () => {
      toast.success("Entries Created Successfully");
      invalidateAllQueries();
    },
    onError: (error) => {
      if (acknowledgementMutation.isError) {
        toast.error("Entries created but acknowledgement failed");
      } else {
        toast.error(error.message);
      }
      console.error("Error submitting timesheet entries:", error);
    },
  });
}
